/**
 * Role permission matrix: the rules engine shared by PlatformFE and RestaurantFE.
 *
 * Pure functions, no React and no network, so they can be unit tested and copied
 * into `src/core/authorization/` of either frontend unchanged.
 * Rules R1–R11 and O1–O4 are documented in ../02-permission-model.md.
 */

// ---------------------------------------------------------------- actions

export const Action = {
  Read: 1,
  Create: 2,
  Update: 4,
  Delete: 8,
  Execute: 16,
} as const;
export type ActionName = 'read' | 'create' | 'update' | 'delete' | 'execute';
export type Mask = number;

export const ACTION_ORDER: readonly ActionName[] = ['read', 'create', 'update', 'delete', 'execute'];
const BIT: Record<ActionName, number> = {
  read: Action.Read, create: Action.Create, update: Action.Update, delete: Action.Delete, execute: Action.Execute,
};
const WRITE_BITS = Action.Create | Action.Update | Action.Delete | Action.Execute;

export const toMask = (names: readonly ActionName[]): Mask => names.reduce((m, n) => m | BIT[n], 0);
export const toNames = (mask: Mask): ActionName[] => ACTION_ORDER.filter((n) => (mask & BIT[n]) !== 0);
export const has = (mask: Mask, action: ActionName): boolean => (mask & BIT[action]) !== 0;

/** R1–R3: keep supported bits; Execute = everything supported; any write implies Read. */
export function normalize(mask: Mask, supported: Mask): Mask {
  let m = mask & supported;
  if (m & Action.Execute) m |= supported;
  if (m & WRITE_BITS) m |= Action.Read & supported;
  return m;
}

/** One click on one cell. R2–R5. */
export function toggle(mask: Mask, action: ActionName, supported: Mask): Mask {
  const bit = BIT[action];
  if (!(supported & bit)) return mask;
  if (!(mask & bit)) return normalize(mask | bit, supported);  // turning on
  if (bit === Action.Read) return 0;                           // R4
  if (bit === Action.Execute) return mask & ~Action.Execute;
  return mask & ~bit & ~Action.Execute;                        // R5
}

// ---------------------------------------------------------------- tree

export interface MatrixNode {
  isSuperAdminOnly?: boolean;
  resourceId: string;
  code: string;
  label: string;
  url?: string | null;
  scopeBase?: string | null;
  supported: Mask;
  /** What the editing admin may grant (actor maximum, R8). */
  grantMax: Mask;
  children: MatrixNode[];
  /** Extra scope areas the page reads on load (display only; enforced by the token). */
  readAlso?: string[] | null;
}

/** Editable state: resourceId -> mask. Only managed resources (visible menus) appear here. */
export type GrantState = Record<string, Mask>;

export const flatten = (nodes: MatrixNode[]): MatrixNode[] => nodes.flatMap(n => [n, ...flatten(n.children)]);

export type CellState = 'on' | 'off' | 'partial' | 'unsupported' | 'locked';

/** State of one action on one row. A root row aggregates itself + its children (R6). */
export function cellState(node: MatrixNode, action: ActionName, state: GrantState): CellState {
  const bit = BIT[action];
  const rows = flatten([node]).filter((n) => n.supported & bit);
  if (rows.length === 0) return 'unsupported';
  const on = rows.filter((n) => (state[n.resourceId] ?? 0) & bit).length;
  if (on === rows.length) return 'on';
  if (rows.every((n) => !(n.grantMax & bit))) return 'locked';
  return on === 0 ? 'off' : 'partial';
}

/**
 * Click on a row cell. A root row applies to itself and every child (R6):
 * it turns ON everywhere unless it is already fully ON, in which case it turns OFF.
 * Cells beyond the actor maximum (R8) are never changed.
 */
export function applyCell(node: MatrixNode, action: ActionName, state: GrantState): GrantState {
  const targets = flatten([node]).filter((n) => n.supported & BIT[action] && n.grantMax & BIT[action]);
  const turnOn = cellState(node, action, state) !== 'on';
  const next = { ...state };
  for (const n of targets) {
    const current = next[n.resourceId] ?? 0;
    if (turnOn === has(current, action)) continue;
    const candidate = toggle(current, action, n.supported);
    next[n.resourceId] = clampToActor(candidate, n);
  }
  return next;
}

/** Column header click: same as applyCell for every visible root (children included). */
export function applyColumn(roots: MatrixNode[], action: ActionName, state: GrantState): GrantState {
  const all = flatten(roots).filter((n) => n.supported & BIT[action] && n.grantMax & BIT[action]);
  const allOn = all.every((n) => has(state[n.resourceId] ?? 0, action));
  let next = { ...state };
  for (const n of all) {
    const current = next[n.resourceId] ?? 0;
    if (allOn !== has(current, action)) continue;
    next = { ...next, [n.resourceId]: clampToActor(toggle(current, action, n.supported), n) };
  }
  return next;
}

export type Preset = 'readOnly' | 'allButExecute' | 'full' | 'clear';

export function applyPreset(nodes: MatrixNode[], preset: Preset, state: GrantState): GrantState {
  const next = { ...state };
  for (const n of flatten(nodes)) {
    const wanted =
      preset === 'clear' ? 0
      : preset === 'readOnly' ? Action.Read
      : preset === 'allButExecute' ? n.supported & ~Action.Execute
      : n.supported;
    next[n.resourceId] = clampToActor(normalize(wanted, n.supported), n);
  }
  return next;
}

/** R8: never exceed what the editing admin holds; re-normalize so R2 still holds. */
function clampToActor(mask: Mask, node: MatrixNode): Mask {
  const clamped = mask & node.grantMax;
  // If Execute was clamped away, keep the other bits (they are still valid on their own).
  return clamped & WRITE_BITS && !(clamped & Action.Read) ? 0 : clamped;
}

// ---------------------------------------------------------------- dependencies (R7)

export interface Dependency { resourceId: string; requiredResourceId: string; inheritRead: boolean; inheritWrite: boolean }

/** Returns the auto-granted additions and who requires them, without changing what the admin ticked. */
export function expandDependencies(state: GrantState, deps: Dependency[], supportedById: Record<string, Mask>) {
  const result: GrantState = { ...state };
  const requiredBy: Record<string, string[]> = {};
  const queue = Object.keys(result).filter((id) => result[id]);
  while (queue.length) {
    const id = queue.shift()!;
    const mask = result[id];
    for (const d of deps.filter((x) => x.resourceId === id)) {
      let add = 0;
      if (d.inheritRead && mask & Action.Read) add |= Action.Read;
      if (d.inheritWrite) add |= mask & WRITE_BITS;
      const supported = supportedById[d.requiredResourceId] ?? Action.Read;
      add = normalize(add, supported);
      const before = result[d.requiredResourceId] ?? 0;
      const after = before | add;
      if (add && !(requiredBy[d.requiredResourceId] ?? []).includes(id)) (requiredBy[d.requiredResourceId] ??= []).push(id);
      if (after !== before) { result[d.requiredResourceId] = after; queue.push(d.requiredResourceId); }
    }
  }
  return { state: result, requiredBy };
}

// ---------------------------------------------------------------- diff / save

export interface Change { resourceId: string; before: ActionName[]; after: ActionName[] }

export function diff(original: GrantState, edited: GrantState): Change[] {
  const ids = new Set([...Object.keys(original), ...Object.keys(edited)]);
  return [...ids]
    .filter((id) => (original[id] ?? 0) !== (edited[id] ?? 0))
    .map((id) => ({ resourceId: id, before: toNames(original[id] ?? 0), after: toNames(edited[id] ?? 0) }));
}

/** Body for PUT /roles/{id}/matrix: every managed resource, absent = no access. */
export function toSaveBody(productCode: string, nodes: MatrixNode[], state: GrantState) {
  return {
    productCode,
    grants: flatten(nodes)
      .map((n) => ({ resourceId: n.resourceId, actions: toNames(normalize(state[n.resourceId] ?? 0, n.supported)) })),
  };
}

// ---------------------------------------------------------------- scopes / runtime checks

/** Token scopes for one resource (section 3 of the model). `legacyWrite` = phase 1–2 compatibility. */
export function scopesFor(scopeBase: string, mask: Mask, legacyWrite = true): string[] {
  const scopes = toNames(mask).map((a) => `${scopeBase}.${a}`);
  if (legacyWrite && mask & WRITE_BITS) scopes.push(`${scopeBase}.write`);
  return scopes;
}

/** Frontend guard used by buttons: `can(scopes, 'restaurant.orders', 'delete')`. Execute = all. */
export function can(scopes: ReadonlySet<string>, scopeBase: string, action: ActionName): boolean {
  if (scopes.has(`${scopeBase}.${action}`) || scopes.has(`${scopeBase}.execute`)) return true;
  return action !== 'read' && action !== 'execute' && scopes.has(`${scopeBase}.write`);
}

// ---------------------------------------------------------------- managed vs open (section 0)

export interface CatalogRow {
  id: string; code: string; kind: 'Menu' | 'Api' | 'Action'; parentId: string | null;
  isHidden: boolean; isPublic: boolean; isActive: boolean; sortOrder: number | null; label: string;
  url?: string | null; scopeBase?: string | null; supported: Mask;
}

export const isManaged = (r: CatalogRow) => r.isActive && r.kind === 'Menu' && !r.isHidden && !r.isPublic;

/** Two-level tree of visible menus, in navigation order. Everything else is "open to everyone". */
export function buildManagedTree(rows: CatalogRow[], grantMax: (r: CatalogRow) => Mask): { roots: MatrixNode[]; openCount: number } {
  const managed = rows.filter(isManaged);
  const byOrder = (a: CatalogRow, b: CatalogRow) =>
    (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999) || a.label.localeCompare(b.label);
  const toNode = (r: CatalogRow): MatrixNode => ({
    resourceId: r.id, code: r.code, label: r.label, url: r.url, scopeBase: r.scopeBase,
    supported: r.supported, grantMax: grantMax(r), children: [],
  });
  const ids = new Set(managed.map((r) => r.id));
  const roots = managed.filter((r) => !r.parentId || !ids.has(r.parentId)).sort(byOrder).map(toNode);
  for (const root of roots)
    root.children = managed.filter((r) => r.parentId === root.resourceId).sort(byOrder).map(toNode);
  return { roots, openCount: rows.filter((r) => r.isActive && !isManaged(r)).length };
}

/** O2: an open resource must not share its ScopeBase with a managed one. */
export function openScopeConflicts(rows: CatalogRow[]): Array<{ open: string; managed: string; scopeBase: string }> {
  const managedByScope = new Map<string, string>();
  rows.filter(isManaged).forEach((r) => r.scopeBase && managedByScope.set(r.scopeBase, r.code));
  return rows
    .filter((r) => r.isActive && !isManaged(r) && r.scopeBase && managedByScope.has(r.scopeBase))
    .map((r) => ({ open: r.code, managed: managedByScope.get(r.scopeBase!)!, scopeBase: r.scopeBase! }));
}
